/** Pan/zoom framing for export crop (matches backend `_fit_export_frame`). */

export const DEFAULT_VIDEO_EXPORT_FRAMING = {
  zoom: 1,
  panX: 0.5,
  panY: 0.5,
};

/** pan 0.5 / 0.5 = crop window centered on the cover-scaled media (matches backend). */
export function centerExportFraming(framing) {
  const f = normalizeVideoExportFraming(framing);
  return { ...f, panX: 0.5, panY: 0.5 };
}

/** Nudge toward center when pan was saved at an edge (avoids blank / off-screen preview). */
export function ensureExportFramingCentered(framing) {
  const f = normalizeVideoExportFraming(framing);
  const edge = f.panX <= 0.02 || f.panX >= 0.98 || f.panY <= 0.02 || f.panY >= 0.98;
  return edge ? centerExportFraming(f) : f;
}

function clampNum(n, lo, hi, fb) {
  const v = Number(n);
  return Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : fb;
}

export function normalizeVideoExportFraming(raw) {
  const d = DEFAULT_VIDEO_EXPORT_FRAMING;
  if (!raw || typeof raw !== 'object') return { ...d };
  return {
    zoom: clampNum(raw.zoom ?? raw.scale, 1, 4, d.zoom),
    panX: clampNum(raw.panX ?? raw.pan_x, 0, 1, d.panX),
    panY: clampNum(raw.panY ?? raw.pan_y, 0, 1, d.panY),
  };
}

export function parseAspectRatio(value) {
  const s = String(value || '16:9').trim();
  const parts = s.split(/[:/]/);
  const w = Number(parts[0]);
  const h = Number(parts[1]);
  if (w > 0 && h > 0) return w / h;
  return 16 / 9;
}

/** Crop preview size presets in the export dialog (max box before aspect fit). */
export const EXPORT_PREVIEW_SIZE_PRESETS = {
  small: { maxW: 260, maxH: 156, label: 'Small' },
  medium: { maxW: 380, maxH: 240, label: 'Medium' },
  large: { maxW: 520, maxH: 360, label: 'Large' },
};

export const EXPORT_PREVIEW_SIZE_DEFAULT = 'medium';

/** Pixel dimensions for server export (must match backend ``EXPORT_ASPECT_RATIO_SIZES``). */
export const EXPORT_TARGET_SIZES = {
  '16:9': { width: 1920, height: 1080 },
  '9:16': { width: 1080, height: 1920 },
  '4:3': { width: 1440, height: 1080 },
  '1:1': { width: 1080, height: 1080 },
  '4:5': { width: 1080, height: 1350 },
  '21:9': { width: 2560, height: 1080 },
  '3:4': { width: 1080, height: 1440 },
  '2:3': { width: 1080, height: 1620 },
};

export function exportTargetSizeForAspect(aspectRatio) {
  const key = String(aspectRatio || '16:9').trim();
  return EXPORT_TARGET_SIZES[key] || EXPORT_TARGET_SIZES['16:9'];
}

export function resolveExportPreviewSizePreset(sizeKey) {
  const key = String(sizeKey || EXPORT_PREVIEW_SIZE_DEFAULT).toLowerCase();
  return EXPORT_PREVIEW_SIZE_PRESETS[key] || EXPORT_PREVIEW_SIZE_PRESETS[EXPORT_PREVIEW_SIZE_DEFAULT];
}

/** Fixed pixel frame that fits inside max box while honoring export aspect ratio. */
export function exportPreviewFramePixels(aspectRatio, maxW = 260, maxH = 156) {
  const ar = parseAspectRatio(aspectRatio);
  let width = maxW;
  let height = width / ar;
  if (height > maxH) {
    height = maxH;
    width = height * ar;
  }
  return {
    width: Math.max(96, Math.round(width)),
    height: Math.max(72, Math.round(height)),
  };
}

export function exportPreviewFramePixelsForSize(aspectRatio, sizeKey = EXPORT_PREVIEW_SIZE_DEFAULT) {
  const { maxW, maxH } = resolveExportPreviewSizePreset(sizeKey);
  return exportPreviewFramePixels(aspectRatio, maxW, maxH);
}

export const EXPORT_FRAMING_ZOOM_MIN = 1;
export const EXPORT_FRAMING_ZOOM_MAX = 4;
export const EXPORT_FRAMING_ZOOM_STEP = 0.12;

export function stepExportFramingZoom(currentZoom, delta) {
  const z = Number(currentZoom) || 1;
  return clampNum(z + delta, EXPORT_FRAMING_ZOOM_MIN, EXPORT_FRAMING_ZOOM_MAX, 1);
}

/**
 * Layout for a cover+crop media layer inside the export frame (pixels).
 * @returns {{ width: number, height: number, left: number, top: number, maxPanX: number, maxPanY: number }}
 */
export function computeExportFramingLayout(
  containerW,
  containerH,
  mediaW,
  mediaH,
  framing,
) {
  const cw = Math.max(1, containerW);
  const ch = Math.max(1, containerH);
  const mw = Math.max(1, mediaW);
  const mh = Math.max(1, mediaH);
  const { zoom, panX, panY } = normalizeVideoExportFraming(framing);

  const coverScale = Math.max(cw / mw, ch / mh);
  const scale = coverScale * zoom;
  const width = mw * scale;
  const height = mh * scale;
  const maxPanX = Math.max(0, width - cw);
  const maxPanY = Math.max(0, height - ch);
  // pan 0 → show left/top of scaled media; pan 1 → show right/bottom (matches backend pan_x * max_left).
  const left = maxPanX > 0 ? -panX * maxPanX : (cw - width) / 2;
  const top = maxPanY > 0 ? -panY * maxPanY : (ch - height) / 2;

  return { width, height, left, top, maxPanX, maxPanY };
}

export function exportFramingToApiPayload(framing) {
  const f = normalizeVideoExportFraming(framing);
  return { zoom: f.zoom, pan_x: f.panX, pan_y: f.panY };
}

/** Whether the crop has room to pan horizontally / vertically at this zoom. */
export function exportFramingPanSlop(
  containerW,
  containerH,
  mediaW,
  mediaH,
  framing,
) {
  const lay = computeExportFramingLayout(containerW, containerH, mediaW, mediaH, framing);
  return {
    canPanX: lay.maxPanX > 1,
    canPanY: lay.maxPanY > 1,
    maxPanX: lay.maxPanX,
    maxPanY: lay.maxPanY,
  };
}
